import { routePageMapQuote, objTealium } from 'utils';

export class TealiumService {
  static loanDetailsPageName = 'vodalend compare: quick quote: loan details';
  static borrowDetailsPageName = 'vodapay: vodalend compare: borrow details';
  static personalDetailsPageName = 'vodapay: vodalend compare: personal details';
  static employmentDetailsPageName = 'vodapay: vodalend compare: employment details';
  static offersPageName = 'vodapay: vodalend compare: loan offers';
  static otpPageName = 'vodapay: vodalend compare: otp screen';
  static documentsPageName = 'vodapay: vodalend compare: documentation required';
  static applicationSubmittedPageName = 'vodapay: vodalend compare: application submitted';
  static offerSuggestionPageName = 'vodalend compare: experience feedback';
  static thankYouPageName = 'vodalend compare: quick quote: thank you';

  static attempt = (func) => {
    try {
      func();
    } catch (err) {
      console.error(err);
    }
  };

  static _commonFields() {
    return {
      page_section: 'vodalend compare',
      page_channel: '[website|mobile app]',
      visitor_login_status: 'logged in',
      page_type: 'webview',
    };
  }

  static async _hashValue(value) {
    const encoder = new TextEncoder();
    const data = encoder.encode(String(value).toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  static sendViewToTealium(obj) {
    this.attempt(() => {
      objTealium.view(obj);
    });
  }

  static sendLinkToTealium(obj) {
    this.attempt(() => {
      objTealium.link(obj);
    });
  }

  /**
   *
   * Form Events
   *
   */
  static onFormError(errorMessage, route) {
    const page_name = routePageMapQuote[route];
    const form_name = page_name?.split(': ').pop();
    this.sendLinkToTealium({
      event_name: 'form_error',
      page_name,
      form_name,
      form_error_name: errorMessage,
      form_error_type: 'user error',
      form_error_code: '-1',
      ...this._commonFields(),
    });
  }

  static onFormInteraction(route) {
    const page_name = routePageMapQuote[route];
    const form_name = page_name?.split(': ').pop();
    this.sendLinkToTealium({
      event_name: 'form_start',
      page_name,
      form_name,
      ...this._commonFields(),
    });
  }

  /**
   *
   * Page name: request (borrow details)
   * Page url: /compare/loan
   *
   * */
  static onBorrowDetailsStart() {
    this.sendViewToTealium({
      event_name: 'page_view, form_start',
      page_name: this.borrowDetailsPageName,
      form_name: 'borrow details',
      ...this._commonFields(),
    });
  }

  static onBorrowDetailsTimedView(completionRate, incompleteFields) {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.borrowDetailsPageName,
      link_id: `${this.borrowDetailsPageName}: next`,
      form_name: 'borrow details',
      form_completion_rate: completionRate,
      form_field_incomplete: incompleteFields,
      ...this._commonFields(),
    });
  }

  static onBorrowDetailsEnd() {
    this.sendLinkToTealium({
      event_name: 'ui_interaction, form_end',
      page_name: this.borrowDetailsPageName,
      link_name: `${this.borrowDetailsPageName}: next`,
      form_name: 'borrow details',
      ...this._commonFields(),
    });
  }

  static onLoanDetailEnd(amount, termAmount, loanUsage) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      service_application_type: 'transaction',
      service_application_event: 'finish',
      loan_amount: amount,
      term_amount: termAmount,
      load_usage: loanUsage,
      page_name: this.loanDetailsPageName,
      service_application_name: 'vodalend compare: quick quote: loan details',
      link_name: 'vodalend compare: quick quote: loan details: continue',
    });
  }

  /**
   *
   * Page name: personal-details
   * Page url: /compare/personal-details
   *
   * */
  static onPersonalDetailsStart() {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.personalDetailsPageName,
      ...this._commonFields(),
    });
  }

  static onPersonalDetailsTimedView(completionRate, incompleteFields) {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.personalDetailsPageName,
      link_id: `${this.personalDetailsPageName}: next`,
      form_name: 'personal details',
      form_completion_rate: completionRate,
      form_field_incomplete: incompleteFields,
      ...this._commonFields(),
    });
  }

  static onPersonalDetailsCheckbox() {
    this.sendLinkToTealium({
      event_name: 'link',
      page_tool_event: 'use',
      page_name: this.personalDetailsPageName,
      page_tool_name: 'vodalend compare: quick quote: personal details: consent',
    });
  }

  static async onPersonalDetailsEnd(mobileNumber, email) {
    try {
      const [hashedMsisdn, hashedEmail] = await Promise.all([
        this._hashValue(mobileNumber),
        this._hashValue(email),
      ]);
      this.sendLinkToTealium({
        event_name: 'ui_interaction, form_end',
        link_id: `${this.personalDetailsPageName}: next`,
        form_name: 'personal details',
        page_name: this.personalDetailsPageName,
        visitor_id_asset_active: hashedMsisdn,
        visitor_id_marketing_email: hashedEmail,
        ...this._commonFields(),
      });
    } catch (err) {
      console.error(err);
    }
  }

  /**
   *
   * Page name: employment-details
   * Page url: /compare/employment-details
   *
   * */
  static onEmploymentDetailsStart() {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.employmentDetailsPageName,
      ...this._commonFields(),
    });
  }

  static onEmploymentDetailsTimedView(completionRate, incompleteFields) {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.employmentDetailsPageName,
      link_id: `${this.employmentDetailsPageName}: next`,
      form_name: 'employment details',
      form_completion_rate: completionRate,
      form_field_incomplete: incompleteFields,
      ...this._commonFields(),
    });
  }

  /**
   *
   * Page name: offers
   * Page url: /compare/offers
   *
   * */
  static onQuotesStart(offers, hashId) {
    const offerList = offers || [];
    this.sendViewToTealium({
      event_name: 'page_view, product_view',
      page_name: this.offersPageName,
      product_name: offerList.map((o) => o.companyName || ''),
      product_quantity: offerList.map(() => 1),
      product_category: 'vodalend compare',
      product_target_segment: 'personal loan',
      transaction_id: hashId,
      ...this._commonFields(),
    });
  }

  static onQuotesEnd(offer, hashId) {
    const totalPayable = offer.isOmf
      ? (offer.installment || 0) * (offer.loanTerm || 0)
      : offer.liveScore?.AmountPayable || 0;
    this.sendLinkToTealium({
      event_name: 'cart_add, ui_interaction',
      page_name: this.offersPageName,
      product_name: offer.isOmf ? 'Old Mutual Finance' : (offer.companyName || ''),
      product_quantity: 1,
      product_price: totalPayable,
      product_category: 'vodalend compare',
      product_target_segment: 'personal loan',
      transaction_id: hashId,
      link_name: `${this.offersPageName}: next`,
      ...this._commonFields(),
    });
  }

  static _omfProductFields(offer, hashId) {
    const totalPayable = offer
      ? (offer.installment || 0) * (offer.loanTerm || 0)
      : 0;
    return {
      product_name: 'Old Mutual Finance',
      product_quantity: 1,
      product_price: totalPayable,
      product_category: 'vodalend compare',
      product_target_segment: 'personal loan',
      transaction_total: totalPayable,
      transaction_id: hashId,
    };
  }

  /**
   *
   * Page name: otp
   * Page url: /compare/otp
   *
   * */
  static onOtpStart(offer, hashId) {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.otpPageName,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onOtpContinue(offer, hashId) {
    this.sendLinkToTealium({
      event_name: 'ui_interaction',
      page_name: this.otpPageName,
      link_name: `${this.otpPageName}: continue`,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onOtpIncorrect(offer, hashId, attemptNumber, maxAttempts) {
    this.sendLinkToTealium({
      event_name: 'form_error',
      page_name: this.otpPageName,
      form_error_name: `incorrect otp ${attemptNumber}/${maxAttempts}`,
      form_error_type: 'user error',
      link_name: `${this.otpPageName}: error`,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onOtpExpired(offer, hashId) {
    this.sendLinkToTealium({
      event_name: 'form_error',
      page_name: this.otpPageName,
      form_error_name: 'expired otp',
      form_error_type: 'user error',
      link_name: `${this.otpPageName}: error`,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onOtpResend(offer, hashId) {
    this.sendLinkToTealium({
      event_name: 'ui_interaction',
      page_name: this.otpPageName,
      link_name: `${this.otpPageName}: resend otp`,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  /**
   *
   * Page name: documents
   * Page url: /compare/documents
   *
   * */
  static onDocumentsStart(offer, hashId) {
    this.sendViewToTealium({
      event_name: 'page_view',
      page_name: this.documentsPageName,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onDocumentsSubmit(offer, hashId) {
    this.sendLinkToTealium({
      event_name: 'ui_interaction',
      page_name: this.documentsPageName,
      link_name: `${this.documentsPageName}: submit`,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  /**
   *
   * Page name: application-submitted
   * Page url: /compare/application-submitted
   *
   * */
  static onApplicationSubmittedStart(offer, hashId) {
    this.sendViewToTealium({
      event_name: 'page_view, compare_get_quote_success, transaction_complete',
      page_name: this.applicationSubmittedPageName,
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onApplicationBannerClick(offer, hashId) {
    this.sendLinkToTealium({
      event_name: 'ui_interaction',
      page_name: this.applicationSubmittedPageName,
      link_name: `${this.applicationSubmittedPageName}: get a quote`,
      campaign_interal_content: 'vodasure | funeral cover - Cover up to 8 adults + 10 children from R56 per month',
      event_category: 'funeral cover',
      ...this._omfProductFields(offer, hashId),
      ...this._commonFields(),
    });
  }

  static onOfferRedirect(companyName) {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: `vodalend compare: quick quote: ${companyName} redirect: success`,
    });
  }

  /**
   *
   * Page name: suggestions
   * Page url: /compare/offers/suggestions
   *
   * */
  static onOfferSuggestionStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: this.offerSuggestionPageName,
      service_application_event: 'start',
      service_application_name: "vodalend compare: not what you're looking for",
      service_application_type: 'query',
    });
  }

  static onOfferSuggestionExit() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: this.offerSuggestionPageName,
      link_name: 'vodalend compare:experience feedback: exit',
    });
  }

  static onOfferSuggestionSubmit(optionSelected) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: this.offerSuggestionPageName,
      link_name: `vodalend compare: experience feedback: ${optionSelected}`,
      service_application_event: 'finish',
      service_application_name: "vodalend compare: not what you're looking for",
      service_application_type: 'query',
    });
  }

  /**
   *
   * Page name: loan glossary
   * Page url: /compare/offers
   *
   * */
  static onLoanGlossary() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: 'vodalend compare: quick quote: product select: loan glossary',
      service_application_name: 'vodalend compare: quick quote: product select: loan glossary',
      service_application_type: 'transaction',
    });
  }

  /**
   *
   * Page name: thank you
   * Page url: /compare/offers/thank-you
   *
   * */
  static onThankYouStart() {
    this.sendViewToTealium({
      page_name: this.thankYouPageName,
      page_event: 'view, vodalend_quote',
      service_application_name: 'vodalend compare: quick quote: thank you',
    });
  }

  /**
   *
   * Page name: offer-failed
   * Page url: /compare/offers/unsuccessful
   *
   * */
  static onApplicationFailedStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: 'vodalend compare: application unsuccessful',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'start',
    });
  }

  static onApplicationFailedExit() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: application unsuccessful',
      link_name: 'vodalend compare: application unsuccessful: exit',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  static onApplicationFailedReportIssue() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: application unsuccessful',
      link_name: 'vodalend compare: application unsuccessful: report an issue',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  /**
   *
   * Page name: technical-error
   *
   * */
  static onTechnicalReportIssue() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: technical error',
      link_name: 'vodalend compare: technical error: report an issue',
      service_application_name: 'vodalend compare: technical error',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  static onTechnicalTryAgain() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: technical error',
      link_name: 'vodalend compare: technical error: try again',
      service_application_name: 'vodalend compare: technical error',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }
}
